
import { AfterViewInit, Directive, Input, OnDestroy, SkipSelf } from "@angular/core";
import { get as _get } from "lodash";
import { Subscription } from "rxjs";
import { map } from "rxjs/operators";
import { RuntimePipe } from "../../../pipes/runtime.pipe";
import { CommonFieldManagerDirective } from "../field-manager.directive";
import { CommonFormManagerDirective } from "../form-manager.directive";

interface aggregateRuntimeOperation {
    runtimeField: string;
    operator: string;
    numberVariableToApply: string;
}

@Directive({
    selector: "[aggregateRuntimes]",
})
export class AggregateRuntimesDirective implements AfterViewInit, OnDestroy {
    @Input() public aggregateRuntimes: string[] = [];
    @Input() public aggregateRuntimesOperations: aggregateRuntimeOperation;

    public formStateSubscriber$: Subscription;

    constructor(@SkipSelf() public formManager: CommonFormManagerDirective,
                public fieldManager: CommonFieldManagerDirective) {
    }

    public ngAfterViewInit() {
        this.formStateSubscriber$ = this.formManager.stateReplaySubject$.pipe(
            map((state: any) => state.formData))
            .subscribe((formData) => {
                const runtimeValues = this.aggregateRuntimes
                    .map((prop: string) => {
                        let propValue = _get(formData, prop.split("::"));
                        if (this.requiresOperation(prop) && !!propValue) {
                            const rawValue = this.unformatRuntimeValue(propValue);
                            const numberVariableToApplyValue = _get(formData, this.aggregateRuntimesOperations.numberVariableToApply.split("::"));
                            const operator = this.aggregateRuntimesOperations.operator;
                            propValue = this.applyOperation(rawValue, numberVariableToApplyValue, operator);
                            return this.rebuildStringValue(propValue);
                        } else {
                            return propValue;
                        }
                    })
                    .filter((values) => !!values);
                if (runtimeValues.length > 0) {
                    const rawAggregate = this.aggregateRawValue(runtimeValues);
                    const stringValue = this.rebuildStringValue(rawAggregate);
                    this.dispatchFieldUpdate(stringValue);
                }
            });
    }

    public ngOnDestroy() {
        this.formStateSubscriber$.unsubscribe();
    }

    public dispatchFieldUpdate(stringValue) {
        const runtimePipe = new RuntimePipe();
        const state = runtimePipe.transform(stringValue);
        this.fieldManager.dispatchFieldUpdate(state);
        this.fieldManager.dispatchFormFieldChange(state);
    }

    public requiresOperation(prop: string) {
        return (!!this.aggregateRuntimesOperations && this.aggregateRuntimesOperations.runtimeField === prop);
    }

    public applyOperation(coefficient, variable, operator) {
        if (operator === "multiply") {
            return coefficient * variable;
        } else if (operator === "add") {
            return coefficient + variable;
        } else if (operator === "subtract") {
            return coefficient - variable;
        } else if (operator === "divide") {
            return coefficient / variable;
        } else {
            return coefficient;
        }
    }

    public unformatRuntimeValue(value) {
        const [minStr, secStr] = value.split(":");
        const minutes = minStr === "" ? 0 : parseInt(minStr, 10);
        const seconds = secStr === "" ? 0 : parseFloat(secStr);
        return (minutes * 60) + seconds;
    }

    public aggregateRawValue(runtimeValues) {
        return runtimeValues.map((value: string) => {
            return this.unformatRuntimeValue(value);
        }).reduce((x, y) => x + y);
    }

    public rebuildStringValue(rawAggregate) {
        const minutes = Math.floor(rawAggregate / 60);
        const seconds = rawAggregate % 60;
        const minStr = !minutes ? "" : minutes.toString();
        let secStr = !seconds ? "00" : seconds.toFixed(2);
        secStr = secStr.split(".")[0].length === 1 ? `0${secStr}` : secStr;
        return `${minStr}:${secStr}`;
    }
}
